import { useState } from 'react';

export default function Hero() {
  const [phone, setPhone] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [queryResult, setQueryResult] = useState<any>(null);

  const handleQuery = async () => {
    if (!phone.trim()) {
      alert('請輸入手機號碼');
      return;
    }

    setIsQuerying(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/query-case-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ phone: phone.trim() })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || '查詢失敗，請稍後再試');
        return;
      }

      if (result.found) {
        if (result.type === 'project_case') {
          // 顯示案件進度
          setQueryResult(result.data);
          setShowResult(true);
        } else if (result.type === 'contact_submission') {
          // 顯示聯絡表單狀態
          setQueryResult({
            case_number: '諮詢評估中',
            status_label: '初步評估',
            current_step: 1,
            next_action: result.data.message,
            updated_at: result.data.created_at,
            isContactSubmission: true
          });
          setShowResult(true);
        }
      } else {
        setQueryResult(null);
        setShowResult(true);
      }
    } catch (error) {
      console.error('查詢失敗:', error);
      alert('查詢失敗，請檢查網路連線後再試');
    } finally {
      setIsQuerying(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQuery();
    }
  };

  const steps = [
    '已送出表單',
    '顧問初審中',
    '補件確認',
    '送審／媒合中',
    '核准／完成'
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/43321df491d4a0e2e55deeaac186a2b1/5f56a47411a87e139f6167125755448a.jpeg"
          alt="專業金融顧問環境"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="max-w-2xl">
          <div className="inline-flex items-center px-4 py-2 bg-amber-500/90 rounded-full mb-6">
            <i className="ri-government-line text-white mr-2"></i>
            <span className="text-white text-sm font-medium">政府補助與專案熱列申請中</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            為您的企業<br />
            注入<span className="text-amber-500">成長動能</span>
          </h1>

          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            朋睿專注於企業多元資金媒合，從青年創業到中小企業轉型，我們提供專業顧問陪同與客製化方案，協助您順利取得關鍵資金。
          </p>

          <div className="flex flex-col sm:flex-row items-start gap-4 mb-16">
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-white text-base font-semibold rounded-lg hover:bg-amber-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              免費預約諮詢
              <i className="ri-arrow-right-line ml-2 text-xl"></i>
            </a>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 px-6 py-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                <i className="ri-search-line text-white text-xl"></i>
                <input
                  type="text"
                  placeholder="輸入手機號碼查詢進度（例：0912-345678）"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="bg-transparent text-white placeholder-white/60 outline-none text-sm w-64"
                />
                <button
                  onClick={handleQuery}
                  disabled={isQuerying}
                  className="px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-md hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {isQuerying ? '查詢中...' : '查詢'}
                </button>
              </div>
              <p className="text-white/60 text-xs px-2">
                為保障隱私，僅顯示案件進度摘要
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="text-4xl font-bold text-white mb-1">500+</div>
              <div className="text-white/70 text-sm">成功核貸案例</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-1">95%</div>
              <div className="text-white/70 text-sm">客戶滿意度</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-1">12+</div>
              <div className="text-white/70 text-sm">合作金融機構</div>
            </div>
          </div>
        </div>
      </div>

      {/* 查詢結果彈窗 */}
      {showResult && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-bounceIn">
            {queryResult ? (
              <>
                {/* 標題區 */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 flex items-center justify-between rounded-t-2xl">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <i className="ri-file-list-3-line"></i>
                    {queryResult.isContactSubmission ? '諮詢狀態' : '您的案件進度'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowResult(false);
                      setQueryResult(null);
                      setPhone('');
                    }}
                    className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                {/* 內容區 */}
                <div className="p-6 space-y-6">
                  {queryResult.isContactSubmission ? (
                    <>
                      {/* 聯絡表單狀態顯示 */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-time-line text-white text-2xl"></i>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">評估中</h4>
                            <p className="text-gray-700 leading-relaxed">
                              {queryResult.next_action}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 提交時間 */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3">
                        <i className="ri-calendar-line text-gray-400"></i>
                        <span>提交時間：{new Date(queryResult.updated_at).toLocaleString('zh-TW', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>

                      {/* 提示訊息 */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <i className="ri-information-line text-blue-500 text-xl flex-shrink-0 mt-0.5"></i>
                          <div className="text-sm text-blue-900">
                            <p className="font-medium mb-1">溫馨提醒</p>
                            <p>顧問將於 1 個工作日內與您聯繫，請保持電話暢通。如有急事，歡迎直接撥打我們的服務專線。</p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 案件資訊卡片 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                            <i className="ri-file-text-line"></i>
                            <span>案件編號</span>
                          </div>
                          <p className="text-gray-900 font-semibold text-lg">{queryResult.case_number}</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                            <i className="ri-flag-line"></i>
                            <span>當前狀態</span>
                          </div>
                          <span className="inline-flex px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                            {queryResult.status_label}
                          </span>
                        </div>
                      </div>

                      {/* 進度條 */}
                      <div className="bg-gray-50 rounded-xl p-5">
                        <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <i className="ri-route-line text-amber-500"></i>
                          案件進度
                        </h4>
                        <div className="space-y-3">
                          {[
                            { step: 1, label: '已送出表單' },
                            { step: 2, label: '顧問初審中' },
                            { step: 3, label: '補件確認' },
                            { step: 4, label: '送審／媒合中' },
                            { step: 5, label: '核准／完成' }
                          ].map((item) => (
                            <div key={item.step} className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm ${
                                item.step <= queryResult.current_step
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-gray-200 text-gray-400'
                              }`}>
                                {item.step <= queryResult.current_step ? (
                                  <i className="ri-check-line text-lg"></i>
                                ) : (
                                  item.step
                                )}
                              </div>
                              <div className="flex-1">
                                <div className={`text-sm font-medium ${
                                  item.step <= queryResult.current_step
                                    ? 'text-gray-900'
                                    : 'text-gray-400'
                                }`}>
                                  {item.label}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 下一步行動 */}
                      {queryResult.next_action && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <i className="ri-lightbulb-line text-blue-500 text-xl flex-shrink-0 mt-0.5"></i>
                            <div>
                              <h5 className="text-sm font-semibold text-blue-900 mb-1">下一步行動建議</h5>
                              <p className="text-sm text-blue-800 leading-relaxed">{queryResult.next_action}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 更新時間 */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3">
                        <i className="ri-time-line text-gray-400"></i>
                        <span>最後更新：{new Date(queryResult.updated_at).toLocaleString('zh-TW', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* 底部按鈕 */}
                <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50 rounded-b-2xl">
                  <a
                    href="/contact"
                    className="text-amber-600 hover:text-amber-700 font-medium text-sm flex items-center gap-1 cursor-pointer"
                  >
                    <i className="ri-customer-service-2-line"></i>
                    聯繫顧問
                  </a>
                  <button
                    onClick={() => {
                      setShowResult(false);
                      setQueryResult(null);
                      setPhone('');
                    }}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                  >
                    關閉
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* 查無資料 */}
                <div className="p-8 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">查無資料</h3>
                  <p className="text-gray-600 mb-6">
                    請確認手機號碼是否正確，或改用「聯絡我們」填寫諮詢
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => {
                        setShowResult(false);
                        setPhone('');
                      }}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      重新查詢
                    </button>
                    <a
                      href="/contact"
                      className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      前往聯絡我們
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
